import L from "leaflet";

const STATE_KEY = "hydrotools:coast-pilot:v1";
const BOOK_COUNT = 10;
const NOAA_BASE = "/api/coast-pilot/files";
const MAX_LOG_LINES = 800;
const MAX_ANCHOR_MARKERS = 2000;
const SPEECH_RATES = [0.8, 1.0, 1.2, 1.5];
const MAX_VOICE_OPTIONS = 6;
const NARRATIVE_LANG_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
];

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    const legacyRouteMode = x.drawRouteMode === true;
    const modeRaw = String(x.interactionMode || (legacyRouteMode ? "route" : "none")).toLowerCase();
    return {
      interactionMode: modeRaw === "point" || modeRaw === "route" ? modeRaw : "none",
      clickRadiusNm: Number.isFinite(Number(x.clickRadiusNm)) ? clamp(Number(x.clickRadiusNm), 1, 80) : 20,
      clickResultLimit: Number.isFinite(Number(x.clickResultLimit)) ? clamp(Math.round(Number(x.clickResultLimit)), 1, 20) : 6,
      routeSampleNm: Number.isFinite(Number(x.routeSampleNm)) ? clamp(Number(x.routeSampleNm), 1, 30) : 8,
      routeRadiusNm: Number.isFinite(Number(x.routeRadiusNm)) ? clamp(Number(x.routeRadiusNm), 1, 80) : 16,
      showAnchors: x.showAnchors === true,
      anchorColorMode: String(x.anchorColorMode || "feature-class") === "single" ? "single" : "feature-class",
      anchorClassEnabled: x.anchorClassEnabled && typeof x.anchorClassEnabled === "object" ? x.anchorClassEnabled : {},
      anchorClassColor: x.anchorClassColor && typeof x.anchorClassColor === "object" ? x.anchorClassColor : {},
      speechVoiceUri: String(x.speechVoiceUri || ""),
    };
  } catch {
    return {
      interactionMode: "none",
      clickRadiusNm: 20,
      clickResultLimit: 6,
      routeSampleNm: 8,
      routeRadiusNm: 16,
      showAnchors: false,
      anchorColorMode: "feature-class",
      anchorClassEnabled: {},
      anchorClassColor: {},
      speechVoiceUri: "",
    };
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {}
}

function toRad(d) {
  return (Number(d) * Math.PI) / 180;
}

function haversineNm(lat1, lon1, lat2, lon2) {
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dp = toRad(lat2 - lat1);
  const dl = toRad(lon2 - lon1);
  const aa = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  const km = 6371 * (2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa)));
  return km * 0.539957;
}

function nmToMeters(nm) {
  return Number(nm || 0) * 1852;
}

function lerp(a, b, t) {
  return a + ((b - a) * t);
}

function metersPerDegLat() {
  return 110540;
}

function metersPerDegLon(lat) {
  return 111320 * Math.max(0.1, Math.cos(toRad(lat)));
}

function offsetPointLatLon(lat, lon, dxMeters, dyMeters) {
  const dLat = dyMeters / metersPerDegLat();
  const dLon = dxMeters / metersPerDegLon(lat);
  return { lat: lat + dLat, lon: lon + dLon };
}

function plainText(s) {
  return String(s || "").replace(/\s+/g, " ").trim();
}

function findAttrCaseInsensitive(el, names) {
  if (!el || !el.attributes) return "";
  const target = new Set(names.map((n) => String(n).toLowerCase()));
  for (let i = 0; i < el.attributes.length; i += 1) {
    const a = el.attributes[i];
    if (target.has(String(a.name || "").toLowerCase())) return String(a.value || "");
  }
  return "";
}

function asNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function nodeChildren(node) {
  if (!node?.children) return [];
  return Array.from(node.children);
}

function chapterLabelFromPath(xmlPath) {
  const id = String(xmlPath || "").replace(/\.xml$/i, "");
  const m = /CPB(\d+)_([A-Z0-9]+)_WEB/i.exec(id);
  if (!m) return id || "--";
  return `Book ${m[1]} ${m[2]}`;
}

function normalizeFeatureClass(v) {
  const raw = String(v || "").trim();
  if (!raw) return "Unknown";
  return raw.replace(/\s+/g, " ");
}

function hslToHex(h, s, l) {
  const hh = ((Number(h) % 360) + 360) % 360;
  const ss = clamp(Number(s), 0, 1);
  const ll = clamp(Number(l), 0, 1);
  const c = (1 - Math.abs((2 * ll) - 1)) * ss;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = ll - (c / 2);
  let rr = 0; let gg = 0; let bb = 0;
  if (hh < 60) [rr, gg, bb] = [c, x, 0];
  else if (hh < 120) [rr, gg, bb] = [x, c, 0];
  else if (hh < 180) [rr, gg, bb] = [0, c, x];
  else if (hh < 240) [rr, gg, bb] = [0, x, c];
  else if (hh < 300) [rr, gg, bb] = [x, 0, c];
  else [rr, gg, bb] = [c, 0, x];
  const toHex = (n) => Math.round((n + m) * 255).toString(16).padStart(2, "0");
  return `#${toHex(rr)}${toHex(gg)}${toHex(bb)}`;
}

function darkenHex(hex, factor = 0.72) {
  const m = /^#?([a-fA-F0-9]{6})$/.exec(String(hex || "").trim());
  if (!m) return "#334155";
  const raw = m[1];
  const r = clamp(Math.round(parseInt(raw.slice(0, 2), 16) * factor), 0, 255);
  const g = clamp(Math.round(parseInt(raw.slice(2, 4), 16) * factor), 0, 255);
  const b = clamp(Math.round(parseInt(raw.slice(4, 6), 16) * factor), 0, 255);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function uniqueAnchorColor(index, total) {
  const t = Math.max(1, Number(total) || 1);
  const h = (Number(index || 0) * (360 / t)) % 360;
  return hslToHex(h, 0.82, 0.56);
}

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function hasPendingTileWork(map) {
    let pending = false;
    try {
      map.eachLayer((layer) => {
        if (pending) return;
        if (typeof layer?.isLoading === "function" && layer.isLoading()) pending = true;
      });
      if (pending) return true;
      const mapEl = map.getContainer?.();
      if (!mapEl) return false;
      const imgs = mapEl.querySelectorAll(".leaflet-tile-container img.leaflet-tile");
      for (let i = 0; i < imgs.length; i += 1) {
        const img = imgs[i];
        if (!img.complete) return true;
      }
    } catch {}
    return pending;
  }

  async function waitForMapTilesToSettle(map, { timeoutMs = 7000, stableMs = 450, pollMs = 90 } = {}) {
    const started = Date.now();
    let stableSince = 0;
    while ((Date.now() - started) < timeoutMs) {
      const pending = hasPendingTileWork(map);
      if (!pending) {
        if (!stableSince) stableSince = Date.now();
        if ((Date.now() - stableSince) >= stableMs) return true;
      } else {
        stableSince = 0;
      }
      await delay(pollMs);
    }
    return false;
  }

function fixedNum(v, digits = 1) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.0";
  return n.toFixed(digits);
}

function topClasses(rows, max = 4) {
  const m = new Map();
  rows.forEach((r) => {
    const k = normalizeFeatureClass(r?.featureClass);
    m.set(k, (m.get(k) || 0) + 1);
  });
  return Array.from(m.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([k]) => k);
}

export const coastPilotTool = {
  id: "coast-pilot-explorer",
  name: "Coast Pilot Explorer",
  init(app) {
    this._rt = this._rt ?? createRuntime(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};

function createRuntime(app) {
  let containerEl = null;
  const state = readState();

  let status = "Idle.";
  let loading = false;
  let indexReady = false;
  let indexBuildPromise = null;
  let indexBuildStats = null;
  let indexProgressPct = 0;

  const pointGuides = [];
  const routeGuides = [];
  const draftRoutePoints = [];
  const speechState = {
    activeKind: null,
    activeGuideId: null,
    activeIndex: 0,
    isPaused: false,
    utterance: null,
  };
  const narrativeSpeechState = {
    activeGuideId: null,
    isPaused: false,
    utterance: null,
  };
  let hoveredAnchorKey = "";

  const interactionLayer = L.layerGroup();
  const guidesLayer = L.layerGroup();
  const anchorLayer = L.layerGroup();
  const hoverLayer = L.layerGroup();
  let draftPointMarker = null;
  let draftPointHalo = null;
  let draftRouteLine = null;
  let draftPoint = null;
  let mapClickHandler = null;
  let mapDblClickHandler = null;
  let mapMoveHandler = null;
  let mapMouseMoveHandler = null;
  let mapWheelHandler = null;
  let voicesChangedHandler = null;
  let diagLog = [];
  let availableVoices = [];
  let voiceOptions = [];

  const anchorIndex = [];
  const anchorByKey = new Map();
  const chapterCache = new Map();
  const chapterManifest = [];
  const anchorKeySet = new Set();

  function nowIso() {
    return new Date().toISOString();
  }

  function hasSpeechSupport() {
    return typeof window !== "undefined"
      && !!window.speechSynthesis
      && typeof window.SpeechSynthesisUtterance !== "undefined";
  }

  function getGuideCollection(kind) {
    return kind === "route" ? routeGuides : pointGuides;
  }

  function getGuide(kind, guideId) {
    const rows = getGuideCollection(kind);
    return rows.find((g) => String(g.id) === String(guideId)) || null;
  }

  function getGuideSpeechIndex(guide) {
    if (!guide || !Array.isArray(guide.rows) || !guide.rows.length) return 0;
    return clamp(Number(guide.speechIndex || 0), 0, guide.rows.length - 1);
  }

  function getGuideSpeechRate(guide) {
    const raw = Number(guide?.speechRate);
    if (!Number.isFinite(raw)) return 1.0;
    return clamp(raw, 0.5, 2.0);
  }

  function getActiveSpeechAnchorKey() {
    if (!speechState.activeKind || !speechState.activeGuideId) return "";
    const guide = getGuide(speechState.activeKind, speechState.activeGuideId);
    if (!guide || !Array.isArray(guide.rows) || !guide.rows.length) return "";
    const row = guide.rows[getGuideSpeechIndex(guide)];
    return String(row?.key || "");
  }

  function syncMapHighlight() {
    const key = hoveredAnchorKey || getActiveSpeechAnchorKey();
    setHoveredAnchor(key || null);
  }

  function chooseVoiceOptions(voices, selectedUri) {
    const all = Array.isArray(voices) ? voices.slice() : [];
    const chosen = [];
    const used = new Set();
    const buckets = [/^en/i, /^es/i, /^fr/i, /^de/i, /^pt/i, /^it/i, /^ja/i, /^ko/i, /^zh/i];
    buckets.forEach((re) => {
      if (chosen.length >= MAX_VOICE_OPTIONS) return;
      const match = all.find((v) => !used.has(v.voiceURI) && re.test(String(v.lang || "")));
      if (match) {
        chosen.push(match);
        used.add(match.voiceURI);
      }
    });
    for (let i = 0; i < all.length && chosen.length < MAX_VOICE_OPTIONS; i += 1) {
      const v = all[i];
      if (used.has(v.voiceURI)) continue;
      chosen.push(v);
      used.add(v.voiceURI);
    }
    if (selectedUri) {
      const selected = all.find((v) => String(v.voiceURI) === String(selectedUri));
      if (selected && !used.has(selected.voiceURI)) {
        if (chosen.length >= MAX_VOICE_OPTIONS) chosen[chosen.length - 1] = selected;
        else chosen.push(selected);
      }
    }
    return chosen.slice(0, MAX_VOICE_OPTIONS);
  }

  function refreshVoices({ rerender = true } = {}) {
    if (!hasSpeechSupport()) {
      availableVoices = [];
      if (rerender) render();
      return;
    }
    try {
      const voices = window.speechSynthesis.getVoices() || [];
      availableVoices = voices.slice().sort((a, b) =>
        `${a.lang || ""} ${a.name || ""}`.localeCompare(`${b.lang || ""} ${b.name || ""}`),
      );
      if (!state.speechVoiceUri) {
        const natasha = availableVoices.find((v) => /natasha/i.test(String(v.name || "")));
        if (natasha?.voiceURI) {
          state.speechVoiceUri = String(natasha.voiceURI);
          saveState(state);
        }
      }
      voiceOptions = chooseVoiceOptions(availableVoices, state.speechVoiceUri);
      if (state.speechVoiceUri && !availableVoices.some((v) => String(v.voiceURI) === String(state.speechVoiceUri))) {
        state.speechVoiceUri = "";
        saveState(state);
      }
      if (rerender) render();
    } catch {
      availableVoices = [];
      voiceOptions = [];
      if (rerender) render();
    }
  }

  function stopSpeech({ cancel = true, rerender = true } = {}) {
    if (cancel && hasSpeechSupport()) {
      try {
        window.speechSynthesis.cancel();
      } catch {}
    }
    speechState.activeKind = null;
    speechState.activeGuideId = null;
    speechState.activeIndex = 0;
    speechState.isPaused = false;
    speechState.utterance = null;
    syncMapHighlight();
    if (rerender) render();
  }

  function narrativeTextFromGuide(guide) {
    const n = guide?.narrativeSummary;
    if (!n) return "";
    const lines = [];
    if (n.title) lines.push(String(n.title));
    if (n.overview) lines.push(String(n.overview));
    if (n.narrative) lines.push(String(n.narrative));
    if (Array.isArray(n.hazards) && n.hazards.length) {
      lines.push(`Key hazards: ${n.hazards.join("; ")}.`);
    }
    if (Array.isArray(n.waypoint_notes) && n.waypoint_notes.length) {
      lines.push(`Waypoint notes: ${n.waypoint_notes.join("; ")}.`);
    }
    return normalizeSpeechText(lines.join(" ").replace(/\s+/g, " ").trim());
  }

  function normalizeSpeechText(text) {
    return String(text || "")
      .replace(/(\d+(?:\.\d+)?)\s*nm\b/gi, "$1 nautical miles")
      .replace(/\bnm\b/gi, "nautical miles");
  }

  function stopNarrativeSpeech({ cancel = true, rerender = true } = {}) {
    if (cancel && hasSpeechSupport()) {
      try {
        window.speechSynthesis.cancel();
      } catch {}
    }
    narrativeSpeechState.activeGuideId = null;
    narrativeSpeechState.isPaused = false;
    narrativeSpeechState.utterance = null;
    if (rerender) render();
  }

  function toggleNarrativeSpeech(guideId) {
    const guide = getGuide("route", guideId);
    if (!guide) return;
    const text = narrativeTextFromGuide(guide);
    if (!text) {
      setStatus("Generate a narrative first.", { level: "warn" });
      return;
    }
    if (!hasSpeechSupport()) {
      setStatus("Text-to-speech is not available in this browser.", { level: "warn" });
      return;
    }
    const synth = window.speechSynthesis;
    const same = String(narrativeSpeechState.activeGuideId) === String(guide.id);
    if (same && narrativeSpeechState.isPaused) {
      try { synth.resume(); } catch {}
      narrativeSpeechState.isPaused = false;
      render();
      return;
    }
    if (same && narrativeSpeechState.utterance && synth.speaking) {
      try { synth.pause(); } catch {}
      narrativeSpeechState.isPaused = true;
      render();
      return;
    }
    stopSpeech({ cancel: true, rerender: false });
    stopNarrativeSpeech({ cancel: false, rerender: false });
    const utter = new window.SpeechSynthesisUtterance(text);
    if (state.speechVoiceUri && availableVoices.length) {
      const voice = availableVoices.find((v) => String(v.voiceURI) === String(state.speechVoiceUri));
      if (voice) utter.voice = voice;
    }
    utter.rate = getGuideSpeechRate(guide);
    narrativeSpeechState.activeGuideId = guide.id;
    narrativeSpeechState.isPaused = false;
    narrativeSpeechState.utterance = utter;
    utter.onend = () => {
      if (narrativeSpeechState.utterance !== utter) return;
      stopNarrativeSpeech({ cancel: false });
    };
    utter.onerror = () => {
      if (narrativeSpeechState.utterance !== utter) return;
      setStatus("Narrative speech playback error.", { level: "warn" });
      stopNarrativeSpeech({ cancel: false });
    };
    synth.speak(utter);
    render();
  }

  async function generateRouteNarrative(guideId) {
    const guide = getGuide("route", guideId);
    if (!guide) return;
    const rows = Array.isArray(guide.rows) ? guide.rows : [];
    if (!rows.length) {
      setStatus("No route excerpts available for narrative generation.", { level: "warn" });
      return;
    }
    try {
      guide.narrativeLoading = true;
      setStatus("Generating narrative summary...");
      render();
      const sorted = rows.slice().sort((a, b) => Number(a.mileNm || 0) - Number(b.mileNm || 0));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const totalNm = Math.max(0, Number(last?.mileNm || 0) - Number(first?.mileNm || 0));
      const classes = topClasses(sorted, 4);
      const hazards = sorted
        .filter((r) => /bar|shoal|reef|rock|ledge|cliff/i.test(`${r.featureName || ""} ${r.featureClass || ""}`))
        .slice(0, 5)
        .map((r) => `${r.featureName} around mile ${fixedNum(r.mileNm, 1)} (${fixedNum(r.distanceNm, 2)} nm off route)`);
      const waypointNotes = sorted.slice(0, 8).map((r) =>
        `Mile ${fixedNum(r.mileNm, 1)}: ${r.featureName} (${r.featureClass || "Unknown"})`,
      );
      const routeName = `Route Guide ${guide.points?.length || 0} waypoints`;
      const lang = String(guide.narrativeLanguage || "en").toLowerCase();
      if (lang.startsWith("es")) {
        guide.narrativeSummary = {
          title: `Resumen Narrativo: ${routeName}`,
          overview: `Esta ruta cubre aproximadamente ${fixedNum(totalNm, 1)} millas náuticas y contiene ${sorted.length} referencias del Coast Pilot. Tipos principales: ${classes.join(", ")}.`,
          narrative: `Desde el inicio cerca de ${first?.featureName || "el primer punto"}, la derrota avanza por la costa y pasa por referencias relevantes hasta ${last?.featureName || "el punto final"}. Los extractos sugieren revisar cada tramo y mantener vigilancia de peligros costeros cercanos a la derrota.`,
          hazards,
          waypoint_notes: waypointNotes,
        };
      } else if (lang.startsWith("fr")) {
        guide.narrativeSummary = {
          title: `Résumé Narratif : ${routeName}`,
          overview: `Cette route couvre environ ${fixedNum(totalNm, 1)} milles nautiques avec ${sorted.length} références Coast Pilot. Types dominants : ${classes.join(", ")}.`,
          narrative: `En partant près de ${first?.featureName || "le premier point"}, l’itinéraire suit la côte et passe des points notables jusqu’à ${last?.featureName || "le point final"}. Les extraits indiquent de vérifier chaque segment et de garder une vigilance renforcée autour des dangers côtiers proches de la route.`,
          hazards,
          waypoint_notes: waypointNotes,
        };
      } else {
        guide.narrativeSummary = {
          title: `Narrative Summary: ${routeName}`,
          overview: `This route spans about ${fixedNum(totalNm, 1)} nm and includes ${sorted.length} Coast Pilot references. Dominant feature classes: ${classes.join(", ")}.`,
          narrative: `Starting near ${first?.featureName || "the first waypoint"}, the route progresses along the coast through key reference points before reaching ${last?.featureName || "the final waypoint"}. The excerpts indicate a coastline-focused transit where segment-by-segment awareness is important, especially where features tighten near the route corridor.`,
          hazards,
          waypoint_notes: waypointNotes,
        };
      }
      guide.narrativeSource = "native";
      guide.narrativeWarning = "";
      setStatus("Narrative summary generated (native).");
    } catch (err) {
      setStatus(`Narrative generation failed: ${err?.message || String(err)}`, { level: "error" });
    } finally {
      guide.narrativeLoading = false;
      render();
    }
  }

  function guideRowSpeechText(kind, row) {
    if (!row) return "";
    const head = kind === "route"
      ? `Mile ${Number(row.mileNm || 0).toFixed(1)}. ${row.featureName || "Feature"}. ${Number(row.distanceNm || 0).toFixed(2)} nautical miles off route.`
      : `${row.featureName || "Feature"}. ${Number(row.distanceNm || 0).toFixed(2)} nautical miles away.`;
    const source = `Book ${row.bookNum || ""}. ${row.chapterTitle || ""}. ${row.paraIndex || "paragraph"}.`;
    return `${head} ${source} ${row.excerpt || ""}`.replace(/\s+/g, " ").trim();
  }

  function featureClassSummary() {
    const counts = new Map();
    for (let i = 0; i < anchorIndex.length; i += 1) {
      const cls = normalizeFeatureClass(anchorIndex[i].featureClass);
      counts.set(cls, (counts.get(cls) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  function ensureClassStylesSeeded() {
    const classes = featureClassSummary();
    let changed = false;
    for (let i = 0; i < classes.length; i += 1) {
      const cls = classes[i].name;
      if (typeof state.anchorClassEnabled[cls] !== "boolean") {
        state.anchorClassEnabled[cls] = true;
        changed = true;
      }
      if (!state.anchorClassColor[cls]) {
        const hue = Math.round((i * (360 / Math.max(1, classes.length))) % 360);
        state.anchorClassColor[cls] = hslToHex(hue, 0.76, 0.56);
        changed = true;
      }
    }
    if (changed) saveState(state);
  }

  function speakGuideRow(kind, guideId, index) {
    const guide = getGuide(kind, guideId);
    if (!guide || !Array.isArray(guide.rows) || !guide.rows.length) return;
    if (!hasSpeechSupport()) {
      setStatus("Text-to-speech is not available in this browser.", { level: "warn" });
      return;
    }
    const synth = window.speechSynthesis;
    stopNarrativeSpeech({ cancel: false, rerender: false });
    const nextIndex = clamp(Number(index || 0), 0, guide.rows.length - 1);
    const row = guide.rows[nextIndex];
    const text = normalizeSpeechText(guideRowSpeechText(kind, row));
    if (!text) return;

    guide.speechIndex = nextIndex;
    speechState.activeKind = kind;
    speechState.activeGuideId = guide.id;
    speechState.activeIndex = nextIndex;
    speechState.isPaused = false;
    syncMapHighlight();

    synth.cancel();
    const utter = new window.SpeechSynthesisUtterance(text);
    utter.rate = getGuideSpeechRate(guide);
    if (state.speechVoiceUri && availableVoices.length) {
      const voice = availableVoices.find((v) => String(v.voiceURI) === String(state.speechVoiceUri));
      if (voice) utter.voice = voice;
    }
    speechState.utterance = utter;
    utter.onend = () => {
      if (speechState.utterance !== utter) return;
      const activeGuide = getGuide(kind, guideId);
      if (!activeGuide || !Array.isArray(activeGuide.rows) || !activeGuide.rows.length) {
        stopSpeech({ cancel: false });
        return;
      }
      const curIdx = getGuideSpeechIndex(activeGuide);
      const nxt = curIdx + 1;
      if (nxt >= activeGuide.rows.length) {
        stopSpeech({ cancel: false });
        return;
      }
      speakGuideRow(kind, guideId, nxt);
    };
    utter.onerror = () => {
      if (speechState.utterance !== utter) return;
      setStatus("Speech playback error.", { level: "warn" });
      stopSpeech({ cancel: false });
    };
    synth.speak(utter);
    render();
  }

  function toggleGuideSpeech(kind, guideId) {
    const guide = getGuide(kind, guideId);
    if (!guide || !Array.isArray(guide.rows) || !guide.rows.length) return;
    if (!hasSpeechSupport()) {
      setStatus("Text-to-speech is not available in this browser.", { level: "warn" });
      return;
    }
    const synth = window.speechSynthesis;
    const sameGuide = speechState.activeKind === kind && String(speechState.activeGuideId) === String(guide.id);
    if (!sameGuide) {
      speakGuideRow(kind, guide.id, getGuideSpeechIndex(guide));
      return;
    }
    if (speechState.isPaused) {
      try {
        synth.resume();
      } catch {}
      speechState.isPaused = false;
      render();
      return;
    }
    if (speechState.utterance && synth.speaking) {
      try {
        synth.pause();
      } catch {}
      speechState.isPaused = true;
      render();
      return;
    }
    speakGuideRow(kind, guide.id, getGuideSpeechIndex(guide));
  }

  function jumpGuideSpeech(kind, guideId, delta) {
    const guide = getGuide(kind, guideId);
    if (!guide || !Array.isArray(guide.rows) || !guide.rows.length) return;
    const idx = clamp(getGuideSpeechIndex(guide) + Number(delta || 0), 0, guide.rows.length - 1);
    guide.speechIndex = idx;
    const sameGuide = speechState.activeKind === kind && String(speechState.activeGuideId) === String(guide.id);
    if (sameGuide) {
      speakGuideRow(kind, guide.id, idx);
    } else {
      render();
    }
  }

  function pushLog(level, message, meta = null) {
    const line = {
      ts: nowIso(),
      level: String(level || "info"),
      message: String(message || ""),
      meta: meta && typeof meta === "object" ? meta : null,
    };
    diagLog.push(line);
    if (diagLog.length > MAX_LOG_LINES) {
      diagLog = diagLog.slice(diagLog.length - MAX_LOG_LINES);
    }
  }

  function setStatus(next, { level = "info", log = true, meta = null } = {}) {
    status = String(next || "");
    if (log) pushLog(level, status, meta);
  }

  function formatLog() {
    if (!diagLog.length) return "No diagnostics yet.";
    return diagLog.map((r) => {
      const suffix = r.meta ? ` | ${JSON.stringify(r.meta)}` : "";
      return `[${r.ts}] ${String(r.level || "info").toUpperCase()} ${r.message}${suffix}`;
    }).join("\n");
  }

  async function copyLogText(text) {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      return true;
    } catch {
      return false;
    }
  }

  function setInteractionMode(mode) {
    const prev = state.interactionMode;
    state.interactionMode = mode === "point" || mode === "route" ? mode : "none";
    if (prev !== state.interactionMode) {
      clearDraftInteraction();
    }
    const map = app.map.instance;
    if (state.interactionMode === "route") map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
    if (state.interactionMode === "point") map.scrollWheelZoom.disable();
    else map.scrollWheelZoom.enable();
    saveState(state);
    applyMapCursor();
    redrawInteraction();
  }

  function applyMapCursor() {
    const el = app.map.instance.getContainer?.();
    if (!el) return;
    el.style.cursor = state.interactionMode === "none" ? "" : "crosshair";
  }

  function clearDraftInteraction() {
    draftRoutePoints.splice(0, draftRoutePoints.length);
    draftPoint = null;
    interactionLayer.clearLayers();
    draftPointMarker = null;
    draftPointHalo = null;
    draftRouteLine = null;
  }

  function redrawInteraction() {
    interactionLayer.clearLayers();
    draftPointMarker = null;
    draftPointHalo = null;
    draftRouteLine = null;
    if (state.interactionMode === "point") {
      if (!draftPoint) return;
      draftPointMarker = L.circleMarker([draftPoint.lat, draftPoint.lon], {
        radius: 5,
        color: "#0f172a",
        weight: 1.5,
        fillColor: "#38bdf8",
        fillOpacity: 0.95,
      }).addTo(interactionLayer);
      draftPointHalo = L.circle([draftPoint.lat, draftPoint.lon], {
        radius: nmToMeters(state.clickRadiusNm),
        color: "#38bdf8",
        weight: 1.1,
        opacity: 0.45,
        fillColor: "#38bdf8",
        fillOpacity: 0.12,
      }).addTo(interactionLayer);
      return;
    }
    if (state.interactionMode !== "route" || !draftRoutePoints.length) {
      return;
    }
    const latLngs = draftRoutePoints.map((p) => [p.lat, p.lon]);
    draftRouteLine = L.polyline(latLngs, {
      color: "#f59e0b",
      weight: 3,
      opacity: 0.95,
      lineCap: "round",
      dashArray: "6 6",
    }).addTo(interactionLayer);
    const corridor = buildRouteCorridor(draftRoutePoints, state.routeRadiusNm, state.routeSampleNm);
    if (corridor.polygon.length >= 3) {
      L.polygon(corridor.polygon.map((p) => [p.lat, p.lon]), {
        color: "#f59e0b",
        weight: 0,
        fillColor: "#f59e0b",
        fillOpacity: 0.08,
      }).addTo(interactionLayer);
    }
    if (corridor.left.length >= 2) {
      L.polyline(corridor.left.map((p) => [p.lat, p.lon]), {
        color: "#f59e0b",
        weight: 1.5,
        opacity: 0.55,
        dashArray: "4 6",
      }).addTo(interactionLayer);
    }
    if (corridor.right.length >= 2) {
      L.polyline(corridor.right.map((p) => [p.lat, p.lon]), {
        color: "#f59e0b",
        weight: 1.5,
        opacity: 0.55,
        dashArray: "4 6",
      }).addTo(interactionLayer);
    }
    draftRoutePoints.forEach((p, i) => {
      L.circleMarker([p.lat, p.lon], {
        radius: 4,
        color: "#0f172a",
        weight: 1.2,
        fillColor: "#f59e0b",
        fillOpacity: 0.95,
      }).bindTooltip(`WP ${i + 1}`, { permanent: false }).addTo(interactionLayer);
    });
  }

  function redrawGuides() {
    guidesLayer.clearLayers();
    pointGuides.forEach((g, gi) => {
      L.circle([g.lat, g.lon], {
        radius: nmToMeters(g.radiusNm),
        color: "#38bdf8",
        weight: 1.1,
        opacity: 0.5,
        fillColor: "#38bdf8",
        fillOpacity: 0.1,
      }).addTo(guidesLayer);
      L.circleMarker([g.lat, g.lon], {
        radius: 5,
        color: "#0f172a",
        weight: 1.5,
        fillColor: "#38bdf8",
        fillOpacity: 0.95,
      })
        .bindTooltip(`P${gi + 1}`, { permanent: false })
        .addTo(guidesLayer);
    });
    routeGuides.forEach((g, gi) => {
      const latLngs = g.points.map((p) => [p.lat, p.lon]);
      L.polyline(latLngs, {
        color: "#f59e0b",
        weight: 3,
        opacity: 0.85,
        lineCap: "round",
      })
        .bindTooltip(`R${gi + 1}`, { permanent: false })
        .addTo(guidesLayer);
      const corridor = buildRouteCorridor(g.points, g.radiusNm, g.sampleNm || state.routeSampleNm);
      if (corridor.polygon.length >= 3) {
        L.polygon(corridor.polygon.map((p) => [p.lat, p.lon]), {
          color: "#f59e0b",
          weight: 0,
          fillColor: "#f59e0b",
          fillOpacity: 0.06,
        }).addTo(guidesLayer);
      }
      if (corridor.left.length >= 2) {
        L.polyline(corridor.left.map((p) => [p.lat, p.lon]), {
          color: "#f59e0b",
          weight: 1.4,
          opacity: 0.45,
          dashArray: "5 6",
        }).addTo(guidesLayer);
      }
      if (corridor.right.length >= 2) {
        L.polyline(corridor.right.map((p) => [p.lat, p.lon]), {
          color: "#f59e0b",
          weight: 1.4,
          opacity: 0.45,
          dashArray: "5 6",
        }).addTo(guidesLayer);
      }
    });
  }

  function updateAnchorLayer() {
    anchorLayer.clearLayers();
    if (!state.showAnchors || !indexReady || !anchorIndex.length) return;
    ensureClassStylesSeeded();
    const map = app.map.instance;
    const b = map.getBounds();
    const rows = [];
    for (let i = 0; i < anchorIndex.length; i += 1) {
      const a = anchorIndex[i];
      const cls = normalizeFeatureClass(a.featureClass);
      if (state.anchorColorMode === "feature-class" && state.anchorClassEnabled[cls] === false) continue;
      if (!b.contains([a.lat, a.lon])) continue;
      rows.push(a);
      if (rows.length >= MAX_ANCHOR_MARKERS) break;
    }
    rows.forEach((a) => {
      const cls = normalizeFeatureClass(a.featureClass);
      const fill = state.anchorColorMode === "feature-class"
        ? (state.anchorClassColor[cls] || "#94a3b8")
        : "#86efac";
      const c = state.anchorColorMode === "feature-class"
        ? { stroke: darkenHex(fill), fill }
        : { stroke: "#22c55e", fill };
      L.circleMarker([a.lat, a.lon], {
        radius: 2.6,
        color: c.stroke,
        weight: 1,
        fillColor: c.fill,
        fillOpacity: 0.7,
      })
        .bindTooltip(`${a.featureName} (${a.featureClass || "Unknown"}, ${a.chapterId})`)
        .addTo(anchorLayer);
    });
    pushLog("info", "Anchor layer refreshed.", { shown: rows.length, total: anchorIndex.length });
  }

  function setHoveredAnchor(key) {
    hoverLayer.clearLayers();
    if (!key) return;
    const a = anchorByKey.get(String(key));
    if (!a) return;
    L.circleMarker([a.lat, a.lon], {
      radius: 7,
      color: "#be123c",
      weight: 2,
      fillColor: "#f43f5e",
      fillOpacity: 0.95,
    })
      .bindTooltip(`${a.featureName} (${a.featureClass || "Unknown"}, ${a.chapterId})`, { permanent: false })
      .addTo(hoverLayer);
  }

  function onMapClick(ev) {
    const lat = Number(ev?.latlng?.lat);
    const lon = Number(ev?.latlng?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    pushLog("info", "Map click received.", { lat, lon, mode: state.interactionMode });
    if (state.interactionMode === "point") {
      draftPoint = { lat, lon };
      redrawInteraction();
      createPointGuide(lat, lon);
      return;
    }
    if (state.interactionMode === "route") {
      const last = draftRoutePoints[draftRoutePoints.length - 1];
      if (last && haversineNm(last.lat, last.lon, lat, lon) < 0.01) {
        return;
      }
      draftRoutePoints.push({ lat, lon });
      redrawInteraction();
      setStatus(`Added route waypoint ${draftRoutePoints.length}. Double-click to finish route.`);
      render();
      return;
    }
  }

  function onMapDoubleClick(ev) {
    if (state.interactionMode !== "route") return;
    try {
      if (ev?.originalEvent) {
        ev.originalEvent.preventDefault?.();
        ev.originalEvent.stopPropagation?.();
      }
      L.DomEvent.stop(ev);
    } catch {}
    finalizeDraftRouteGuide();
  }

  function onMapMouseMove(ev) {
    if (state.interactionMode !== "point") return;
    const lat = Number(ev?.latlng?.lat);
    const lon = Number(ev?.latlng?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    draftPoint = { lat, lon };
    redrawInteraction();
  }

  async function fetchXml(url) {
    pushLog("info", "Fetching XML.", { url });
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status} ${url}`);
    return await res.text();
  }

  function parseXml(text, label = "XML") {
    const doc = new DOMParser().parseFromString(text, "application/xml");
    if (doc.querySelector("parsererror")) {
      throw new Error(`${label} parse error`);
    }
    return doc;
  }

  async function loadBookManifest(bookNum) {
    const xml = await fetchXml(`${NOAA_BASE}/CP${bookNum}.xml`);
    const doc = parseXml(xml, `CP${bookNum}`);
    const root = doc.querySelector("CoastPilot");
    const chapters = Array.from(doc.querySelectorAll("Chapter"));
    const out = [];
    chapters.forEach((ch) => {
      const xmlPath = plainText(ch.querySelector("xmlpath")?.textContent || "");
      if (!xmlPath || xmlPath.toLowerCase() === "none") return;
      const title = plainText(ch.querySelector("Title")?.textContent || chapterLabelFromPath(xmlPath));
      const desc = plainText(ch.querySelector("Desc")?.textContent || "");
      out.push({
        bookNum,
        bookEdition: plainText(root?.querySelector("Edition")?.textContent || ""),
        bookYear: plainText(root?.querySelector("Year")?.textContent || ""),
        title,
        desc,
        xmlPath,
        chapterId: String(xmlPath).replace(/\.xml$/i, ""),
        chapterUrl: `${NOAA_BASE}/cp${bookNum}/${xmlPath}`,
      });
    });
    return out;
  }

  function collectGeoLocNodes(node) {
    const out = [];
    if (!node) return out;
    const all = node.getElementsByTagName("*");
    for (let i = 0; i < all.length; i += 1) {
      const n = all[i];
      if (String(n.nodeName || "").toUpperCase() === "CP_GEO_LOC") out.push(n);
    }
    return out;
  }

  function parseChapterAnchors(chapterMeta, xmlText) {
    const doc = parseXml(xmlText, chapterMeta.chapterId);
    const chapterNode = doc.querySelector("Chapter");
    if (!chapterNode) return [];
    const chapterTitle = plainText(doc.querySelector("chapterTitle")?.textContent || chapterMeta.title);
    const rows = [];
    const chapterMedia = [];
    let mostRecentMedia = null;

    nodeChildren(chapterNode).forEach((child) => {
      const nodeName = String(child.nodeName || "").toLowerCase();
      if (nodeName === "image_detail" || nodeName === "table_detail") {
        const file = plainText(findAttrCaseInsensitive(child, ["OriginalFile", "originalfile"]));
        if (file) {
          const media = {
            kind: nodeName === "image_detail" ? "image" : "table",
            file,
            url: `${NOAA_BASE}/cp${chapterMeta.bookNum}/images/${file}`,
          };
          chapterMedia.push(media);
          mostRecentMedia = media;
        }
        return;
      }
      if (nodeName !== "paragraph") return;
      const paraIndex = plainText(child.querySelector("paraIndex")?.textContent || "");
      const paraTextNode = child.querySelector("paraText");
      const excerpt = plainText(paraTextNode?.textContent || child.textContent || "");
      if (!excerpt) return;
      const locNodes = collectGeoLocNodes(paraTextNode || child);
      locNodes.forEach((loc) => {
        const lat = asNumber(findAttrCaseInsensitive(loc, ["lat_dec", "Lat_Dec"]));
        const lon = asNumber(findAttrCaseInsensitive(loc, ["long_dec", "Long_Dec"]));
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        const featureName = plainText(findAttrCaseInsensitive(loc, ["feature_name", "Feature_Name"]) || loc.textContent || "");
        const featureClass = normalizeFeatureClass(findAttrCaseInsensitive(loc, ["feature_class", "Feature_Class"]));
        const key = `${chapterMeta.chapterId}|${paraIndex}|${lat.toFixed(5)}|${lon.toFixed(5)}|${featureName}|${featureClass}`;
        if (anchorKeySet.has(key)) return;
        anchorKeySet.add(key);
        rows.push({
          key,
          lat,
          lon,
          featureName: featureName || "(Unnamed feature)",
          featureClass,
          excerpt,
          paraIndex,
          bookNum: chapterMeta.bookNum,
          chapterId: chapterMeta.chapterId,
          chapterTitle,
          chapterDesc: chapterMeta.desc || "",
          chapterUrl: chapterMeta.chapterUrl,
          mediaUrl: mostRecentMedia?.url || chapterMedia[0]?.url || "",
          mediaKind: mostRecentMedia?.kind || chapterMedia[0]?.kind || "",
        });
      });
    });
    return rows;
  }

  async function ensureIndexBuilt() {
    if (indexReady) return;
    if (indexBuildPromise) return await indexBuildPromise;
    indexBuildPromise = (async () => {
      const startedAt = Date.now();
      let chapterCount = 0;
      let errorCount = 0;
      indexProgressPct = 2;
      setStatus("Loading Coast Pilot book manifests...");
      render();
      for (let b = 1; b <= BOOK_COUNT; b += 1) {
        try {
          const chapters = await loadBookManifest(b);
          chapterManifest.push(...chapters);
          pushLog("info", `Loaded manifest for book ${b}.`, { chapters: chapters.length });
        } catch (err) {
          errorCount += 1;
          pushLog("error", `Failed loading book manifest ${b}.`, { error: String(err?.message || err) });
        }
        indexProgressPct = Math.min(18, Math.round((b / BOOK_COUNT) * 18));
        render();
      }
      setStatus(`Indexing Coast Pilot chapters (${chapterManifest.length.toLocaleString()} total)...`);
      render();
      for (let i = 0; i < chapterManifest.length; i += 1) {
        const ch = chapterManifest[i];
        try {
          let xml = chapterCache.get(ch.chapterUrl);
          if (!xml) {
            xml = await fetchXml(ch.chapterUrl);
            chapterCache.set(ch.chapterUrl, xml);
          }
          const rows = parseChapterAnchors(ch, xml);
          rows.forEach((r) => {
            anchorIndex.push(r);
            anchorByKey.set(String(r.key), r);
          });
          chapterCount += 1;
          if (i % 20 === 0) {
            setStatus(`Indexed ${i + 1}/${chapterManifest.length} chapters, ${anchorIndex.length.toLocaleString()} anchors...`);
          }
          indexProgressPct = chapterManifest.length
            ? Math.min(98, 18 + Math.round(((i + 1) / chapterManifest.length) * 80))
            : 98;
          render();
        } catch (err) {
          errorCount += 1;
          pushLog("error", "Failed indexing chapter.", { chapterId: ch?.chapterId, chapterUrl: ch?.chapterUrl, error: String(err?.message || err) });
        }
      }
      indexBuildStats = {
        chapterCount,
        errorCount,
        anchors: anchorIndex.length,
        elapsedSec: (Date.now() - startedAt) / 1000,
      };
      indexReady = true;
      indexProgressPct = 100;
      ensureClassStylesSeeded();
      setStatus(`Coast Pilot index ready: ${anchorIndex.length.toLocaleString()} geotagged anchors.`);
      updateAnchorLayer();
      render();
      indexProgressPct = 0;
    })();
    return await indexBuildPromise;
  }

  function anchorsWithinPointHalo(lat, lon, radiusNm) {
    const rows = [];
    const radius = Math.max(1, Number(radiusNm) || 20);
    for (let i = 0; i < anchorIndex.length; i += 1) {
      const a = anchorIndex[i];
      const d = haversineNm(lat, lon, a.lat, a.lon);
      if (d > radius) continue;
      rows.push({
        ...a,
        distanceNm: d,
      });
    }
    rows.sort((a, b) => a.distanceNm - b.distanceNm);
    return rows;
  }

  function anchorsWithinRouteHalo(points, radiusNm, sampleNm) {
    const sampled = sampleRoutePoints(points, sampleNm);
    if (!sampled.length) return { sampled: [], rows: [] };
    const radius = Math.max(1, Number(radiusNm) || 16);
    const rows = [];
    for (let i = 0; i < anchorIndex.length; i += 1) {
      const a = anchorIndex[i];
      let minDist = Number.POSITIVE_INFINITY;
      let firstMile = null;
      for (let j = 0; j < sampled.length; j += 1) {
        const s = sampled[j];
        const d = haversineNm(a.lat, a.lon, s.lat, s.lon);
        if (d < minDist) minDist = d;
        if (d <= radius && firstMile == null) firstMile = s.mileNm;
      }
      if (firstMile == null) continue;
      rows.push({
        ...a,
        distanceNm: minDist,
        mileNm: firstMile,
      });
    }
    rows.sort((a, b) => (a.mileNm - b.mileNm) || (a.distanceNm - b.distanceNm));
    return { sampled, rows };
  }

  async function createPointGuide(lat, lon) {
    try {
      loading = true;
      setStatus("Searching Coast Pilot excerpts in point halo...");
      render();
      await ensureIndexBuilt();
      const rows = anchorsWithinPointHalo(lat, lon, state.clickRadiusNm);
      const guide = {
        id: `point-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
        createdAt: Date.now(),
        lat,
        lon,
        radiusNm: state.clickRadiusNm,
        rows,
        speechIndex: 0,
        speechRate: 1.0,
      };
      pointGuides.unshift(guide);
      redrawGuides();
      if (!rows.length) {
        setStatus(`No Coast Pilot geotags found within ${state.clickRadiusNm} nm.`);
      } else {
        setStatus(`Point guide created with ${rows.length} anchors in halo.`);
      }
      setInteractionMode("none");
    } catch (err) {
      setStatus(`Coast Pilot lookup failed: ${err?.message || String(err)}`);
    } finally {
      loading = false;
      render();
    }
  }

  function sampleRoutePoints(points, spacingNm) {
    if (!Array.isArray(points) || points.length < 2) return [];
    const spacing = Math.max(0.5, Number(spacingNm) || 8);
    const out = [];
    let cumulative = 0;
    out.push({ lat: points[0].lat, lon: points[0].lon, mileNm: 0 });
    for (let i = 1; i < points.length; i += 1) {
      const a = points[i - 1];
      const b = points[i];
      const legNm = haversineNm(a.lat, a.lon, b.lat, b.lon);
      const steps = Math.max(1, Math.floor(legNm / spacing));
      for (let s = 1; s <= steps; s += 1) {
        const t = s / steps;
        const lat = lerp(a.lat, b.lat, t);
        const lon = lerp(a.lon, b.lon, t);
        out.push({ lat, lon, mileNm: cumulative + (legNm * t) });
      }
      cumulative += legNm;
      out.push({ lat: b.lat, lon: b.lon, mileNm: cumulative });
    }
    return out;
  }

  function buildRouteCorridor(points, radiusNm, sampleNm) {
    const sampled = sampleRoutePoints(points, sampleNm);
    const radiusMeters = nmToMeters(Math.max(1, Number(radiusNm) || 16));
    if (sampled.length < 2 || radiusMeters <= 0) {
      return { sampled, left: [], right: [], polygon: [] };
    }
    const left = [];
    const right = [];
    for (let i = 0; i < sampled.length; i += 1) {
      const cur = sampled[i];
      const prev = sampled[Math.max(0, i - 1)];
      const next = sampled[Math.min(sampled.length - 1, i + 1)];
      const dxPrev = (cur.lon - prev.lon) * metersPerDegLon(cur.lat);
      const dyPrev = (cur.lat - prev.lat) * metersPerDegLat();
      const dxNext = (next.lon - cur.lon) * metersPerDegLon(cur.lat);
      const dyNext = (next.lat - cur.lat) * metersPerDegLat();
      let tx = dxPrev + dxNext;
      let ty = dyPrev + dyNext;
      const tLen = Math.hypot(tx, ty);
      if (tLen < 1e-6) {
        tx = dxNext || dxPrev;
        ty = dyNext || dyPrev;
      }
      const len = Math.max(1e-6, Math.hypot(tx, ty));
      const nx = -ty / len;
      const ny = tx / len;
      const l = offsetPointLatLon(cur.lat, cur.lon, nx * radiusMeters, ny * radiusMeters);
      const r = offsetPointLatLon(cur.lat, cur.lon, -nx * radiusMeters, -ny * radiusMeters);
      left.push(l);
      right.push(r);
    }
    const polygon = [...left, ...right.slice().reverse()];
    return { sampled, left, right, polygon };
  }

  async function generateRouteGuide() {
    if (draftRoutePoints.length < 2) {
      setStatus("Route guide requires at least 2 waypoints.");
      render();
      return;
    }
    try {
      loading = true;
      setStatus("Building route guide from route halo...");
      render();
      await ensureIndexBuilt();
      const routePoints = draftRoutePoints.map((p) => ({ ...p }));
      const { sampled, rows } = anchorsWithinRouteHalo(routePoints, state.routeRadiusNm, state.routeSampleNm);
      const guide = {
        id: `route-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
        createdAt: Date.now(),
        points: routePoints,
        haloSample: sampled,
        radiusNm: state.routeRadiusNm,
        sampleNm: state.routeSampleNm,
        rows,
        speechIndex: 0,
        speechRate: 1.0,
        narrativeLanguage: "en",
        narrativeSummary: null,
        narrativeSource: "",
        narrativeWarning: "",
        narrativeLoading: false,
      };
      routeGuides.unshift(guide);
      redrawGuides();
      if (!rows.length) {
        setStatus(`No route-guide excerpts found within ${state.routeRadiusNm} nm of sampled route points.`);
      } else {
        setStatus(`Route guide created with ${rows.length} anchors in halo corridor.`);
      }
      clearDraftInteraction();
      setInteractionMode("none");
    } catch (err) {
      setStatus(`Route guide failed: ${err?.message || String(err)}`);
    } finally {
      loading = false;
      render();
    }
  }

  function finalizeDraftRouteGuide() {
    if (draftRoutePoints.length < 2) {
      setStatus("Need at least 2 route points before finishing.");
      render();
      return;
    }
    generateRouteGuide();
  }

  function clearRoute() {
    stopSpeech({ cancel: true, rerender: false });
    stopNarrativeSpeech({ cancel: false, rerender: false });
    pointGuides.splice(0, pointGuides.length);
    routeGuides.splice(0, routeGuides.length);
    clearDraftInteraction();
    redrawGuides();
    setStatus("Cleared all point and route guides.");
    render();
  }

  async function renderRouteMapSnapshot(guide, exportRows) {
    const map = app.map.instance;
    const mapEl = map.getContainer?.();
    if (!map || !mapEl) throw new Error("Map not available for export.");
    const originalCenter = map.getCenter();
    const originalZoom = map.getZoom();
    const exportLayer = L.layerGroup().addTo(map);
    try {
      const bounds = [];
      const routeLatLng = (guide.points || []).map((p) => [p.lat, p.lon]);
      if (routeLatLng.length >= 2) {
        L.polyline(routeLatLng, { color: "#111827", weight: 3, opacity: 0.92 }).addTo(exportLayer);
        routeLatLng.forEach((p) => bounds.push(p));
      }
      (exportRows || []).forEach((r) => {
        L.circleMarker([r.lat, r.lon], {
          radius: 5,
          color: darkenHex(r.exportColor),
          weight: 1.2,
          fillColor: r.exportColor,
          fillOpacity: 0.96,
        }).addTo(exportLayer);
        bounds.push([r.lat, r.lon]);
      });
      if (bounds.length) {
        map.fitBounds(bounds, { padding: [36, 36], animate: false });
      }
      map.invalidateSize(false);
      await waitForMapTilesToSettle(map);
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(mapEl, {
        backgroundColor: "#ffffff",
        useCORS: true,
        scale: 2,
      });
      return canvas.toDataURL("image/jpeg", 0.92);
    } finally {
      try { map.removeLayer(exportLayer); } catch {}
      try { map.setView(originalCenter, originalZoom, { animate: false }); } catch {}
      try { map.invalidateSize(false); } catch {}
    }
  }

  async function renderPointMapSnapshot(guide, exportRows) {
    const map = app.map.instance;
    const mapEl = map.getContainer?.();
    if (!map || !mapEl) throw new Error("Map not available for export.");
    const originalCenter = map.getCenter();
    const originalZoom = map.getZoom();
    const exportLayer = L.layerGroup().addTo(map);
    try {
      const bounds = [];
      const point = [Number(guide.lat), Number(guide.lon)];
      if (Number.isFinite(point[0]) && Number.isFinite(point[1])) {
        L.circleMarker(point, {
          radius: 5,
          color: "#0f172a",
          weight: 1.2,
          fillColor: "#22d3ee",
          fillOpacity: 0.95,
        }).addTo(exportLayer);
        bounds.push(point);
        const rMeters = nmToMeters(Number(guide.radiusNm) || 1);
        const n = offsetPointLatLon(point[0], point[1], 0, rMeters);
        const s = offsetPointLatLon(point[0], point[1], 0, -rMeters);
        bounds.push([n.lat, n.lon], [s.lat, s.lon]);
      }
      (exportRows || []).forEach((r) => {
        L.circleMarker([r.lat, r.lon], {
          radius: 5,
          color: darkenHex(r.exportColor),
          weight: 1.2,
          fillColor: r.exportColor,
          fillOpacity: 0.96,
        }).addTo(exportLayer);
        bounds.push([r.lat, r.lon]);
      });
      if (bounds.length) {
        map.fitBounds(bounds, { padding: [36, 36], animate: false });
      }
      map.invalidateSize(false);
      await waitForMapTilesToSettle(map);
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(mapEl, {
        backgroundColor: "#ffffff",
        useCORS: true,
        scale: 2,
      });
      return canvas.toDataURL("image/jpeg", 0.92);
    } finally {
      try { map.removeLayer(exportLayer); } catch {}
      try { map.setView(originalCenter, originalZoom, { animate: false }); } catch {}
      try { map.invalidateSize(false); } catch {}
    }
  }

  function drawLegendPage(
    doc,
    entries,
    {
      mapImage = "",
      title = "Route Guide Map + Anchor Legend",
      titleCentered = false,
      titleSize = 14,
      headerLines = [],
    } = {},
  ) {
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 24;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(Number(titleSize) > 0 ? Number(titleSize) : 14);
    const titleText = String(title || "Map + Anchor Legend");
    const titleY = 28;
    if (titleCentered) {
      doc.text(titleText, pageW / 2, titleY, { align: "center" });
    } else {
      doc.text(titleText, margin, titleY);
    }
    let metaY = titleY + 16;
    if (Array.isArray(headerLines) && headerLines.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      headerLines.forEach((line) => {
        doc.text(String(line || ""), pageW / 2, metaY, { align: "center" });
        metaY += 13;
      });
    }
    const mapY = Math.max(40, metaY + 8);
    const mapH = Math.max(280, 482 - (mapY - 40));
    doc.addImage(mapImage, "JPEG", margin, mapY, pageW - (margin * 2), mapH);
    const top = mapY + mapH + 18;
    const colCount = 3;
    const colGap = 12;
    const colW = ((pageW - (margin * 2)) - (colGap * (colCount - 1))) / colCount;
    const rowH = 12;
    const legendTitleY = top;
    const rowsStartY = legendTitleY + 14;
    const maxRows = Math.max(1, Math.floor((pageH - rowsStartY - margin - 8) / rowH));
    let idx = 0;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Legend", margin, legendTitleY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    for (let col = 0; col < colCount; col += 1) {
      for (let row = 0; row < maxRows && idx < entries.length; row += 1) {
        const x = margin + (col * (colW + colGap));
        const y = rowsStartY + (row * rowH);
        const e = entries[idx];
        doc.setFillColor(e.exportColor);
        doc.rect(x, y - 7, 7, 7, "F");
        doc.setDrawColor(15, 23, 42);
        doc.rect(x, y - 7, 7, 7, "S");
        const label = `${idx + 1}. ${e.featureName} (${Number(e.mileNm || 0).toFixed(1)} nm)`;
        const oneLine = String(doc.splitTextToSize(label, colW - 15)[0] || "");
        doc.text(oneLine, x + 11, y - 1);
        idx += 1;
      }
    }
    if (idx < entries.length) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(7);
      doc.text(`Legend truncated: ${entries.length - idx} more anchors listed in excerpts below.`, margin, pageH - margin + 2);
    }
  }

  async function exportRouteGuidePdf(guideId) {
    const guide = getGuide("route", guideId);
    if (!guide) {
      setStatus("Route guide not found.", { level: "warn" });
      return;
    }
    const rows = Array.isArray(guide.rows) ? guide.rows : [];
    const exportRows = rows.map((r, i) => ({
      ...r,
      exportColor: uniqueAnchorColor(i, rows.length || 1),
    }));
    try {
      loading = true;
      setStatus("Preparing route guide PDF...");
      render();
      const mapImage = await renderRouteMapSnapshot(guide, exportRows);
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      drawLegendPage(doc, exportRows, { mapImage, title: "Route Guide Map + Anchor Legend" });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 28;
      let y = 40;
      const writeLine = (txt, font = "normal", size = 10, x = margin) => {
        doc.setFont("helvetica", font);
        doc.setFontSize(size);
        const lines = doc.splitTextToSize(String(txt || ""), pageW - margin - x);
        const lh = size + 3;
        for (let i = 0; i < lines.length; i += 1) {
          if (y + lh > pageH - margin) {
            doc.addPage("letter", "portrait");
            y = 40;
          }
          doc.text(lines[i], x, y);
          y += lh;
        }
      };

      doc.addPage("letter", "portrait");
      writeLine("Route Guide Excerpts", "bold", 14);
      y += 4;
      if (!exportRows.length) {
        writeLine("No anchors/excerpts found for this route guide.", "normal", 11);
      } else {
        exportRows.forEach((r, i) => {
          if (y > pageH - 120) {
            doc.addPage("letter", "portrait");
            y = 40;
          }
          const baseY = y;
          doc.setFillColor(r.exportColor);
          doc.rect(margin, baseY - 9, 8, 8, "F");
          doc.setDrawColor(15, 23, 42);
          doc.rect(margin, baseY - 9, 8, 8, "S");
          writeLine(`${i + 1}. ${r.featureName} | Mile ${Number(r.mileNm || 0).toFixed(1)} | ${Number(r.distanceNm || 0).toFixed(2)} nm off-route | ${r.featureClass || "Unknown"}`, "bold", 10, margin + 12);
          writeLine(`Source: Book ${r.bookNum} | ${r.chapterTitle} | ${r.paraIndex || "para"}`, "normal", 9);
          writeLine(r.excerpt || "", "normal", 10);
          y += 6;
        });
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      doc.save(`route_guide_${stamp}.pdf`);
      setStatus(`Exported route guide PDF (${exportRows.length} anchors).`);
    } catch (err) {
      setStatus(`Route guide PDF export failed: ${err?.message || String(err)}`, { level: "error" });
    } finally {
      loading = false;
      render();
    }
  }

  async function exportPointGuidePdf(guideId) {
    const guide = getGuide("point", guideId);
    if (!guide) {
      setStatus("Point guide not found.", { level: "warn" });
      return;
    }
    const rows = Array.isArray(guide.rows) ? guide.rows : [];
    const exportRows = rows.map((r, i) => ({
      ...r,
      exportColor: uniqueAnchorColor(i, rows.length || 1),
      mileNm: 0,
    }));
    try {
      loading = true;
      setStatus("Preparing point guide PDF...");
      render();
      const mapImage = await renderPointMapSnapshot(guide, exportRows);
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const created = Number.isFinite(Number(guide.createdAt)) ? new Date(Number(guide.createdAt)) : new Date();
      const nearestNm = exportRows.length
        ? Math.min(...exportRows.map((r) => Number(r.distanceNm || Number.POSITIVE_INFINITY)))
        : null;
      const classCount = new Set(exportRows.map((r) => normalizeFeatureClass(r?.featureClass))).size;
      const headerLines = [
        `Lat: ${Number(guide.lat).toFixed(5)}`,
        `Lon: ${Number(guide.lon).toFixed(5)}`,
        `Radius: ${Number(guide.radiusNm || 0).toFixed(1)} nm`,
        `Date: ${created.toLocaleString()}`,
        `Anchors Matched: ${exportRows.length}`,
        `Feature Classes: ${classCount}`,
        nearestNm != null && Number.isFinite(nearestNm) ? `Nearest Anchor: ${nearestNm.toFixed(2)} nm` : "",
      ].filter(Boolean);
      drawLegendPage(doc, exportRows, {
        mapImage,
        title: "Coast Pilot Detail Report",
        titleCentered: true,
        titleSize: 22,
        headerLines,
      });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 28;
      let y = 40;
      const writeLine = (txt, font = "normal", size = 10, x = margin) => {
        doc.setFont("helvetica", font);
        doc.setFontSize(size);
        const lines = doc.splitTextToSize(String(txt || ""), pageW - margin - x);
        const lh = size + 3;
        for (let i = 0; i < lines.length; i += 1) {
          if (y + lh > pageH - margin) {
            doc.addPage("letter", "portrait");
            y = 40;
          }
          doc.text(lines[i], x, y);
          y += lh;
        }
      };

      doc.addPage("letter", "portrait");
      writeLine("Coast Pilot Detail Excerpts", "bold", 14);
      y += 4;
      if (!exportRows.length) {
        writeLine("No anchors/excerpts found for this click excerpt.", "normal", 11);
      } else {
        exportRows.forEach((r, i) => {
          if (y > pageH - 120) {
            doc.addPage("letter", "portrait");
            y = 40;
          }
          const baseY = y;
          doc.setFillColor(r.exportColor);
          doc.rect(margin, baseY - 9, 8, 8, "F");
          doc.setDrawColor(15, 23, 42);
          doc.rect(margin, baseY - 9, 8, 8, "S");
          writeLine(`${i + 1}. ${r.featureName} | ${Number(r.distanceNm || 0).toFixed(2)} nm from point | ${r.featureClass || "Unknown"}`, "bold", 10, margin + 12);
          writeLine(`Source: Book ${r.bookNum} | ${r.chapterTitle} | ${r.paraIndex || "para"}`, "normal", 9);
          writeLine(r.excerpt || "", "normal", 10);
          y += 6;
        });
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      doc.save(`click_excerpt_${stamp}.pdf`);
      setStatus(`Exported click excerpt PDF (${exportRows.length} anchors).`);
    } catch (err) {
      setStatus(`Click excerpt PDF export failed: ${err?.message || String(err)}`, { level: "error" });
    } finally {
      loading = false;
      render();
    }
  }

  function render() {
    if (!containerEl) return;
    const classRows = indexReady ? featureClassSummary() : [];
    const isIndexLoading = !!indexBuildPromise && !indexReady;
    const progressValue = isIndexLoading ? clamp(Number(indexProgressPct || 0), 0, 100) : (indexReady ? 100 : 0);
    const statsText = indexReady
      ? `Indexed ${Number(indexBuildStats?.anchors || 0).toLocaleString()} anchors across ${Number(indexBuildStats?.chapterCount || 0).toLocaleString()} chapters.`
      : "Index not built yet.";
    containerEl.innerHTML = `
      <details class="card lt-collapsible" open style="padding:8px 10px;">
        <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">
          Controls
        </summary>
        <div class="small" style="margin-top:6px;">Click map for local excerpts, or draw a route and generate a TripTik-style route guide.</div>
        <div class="row lm-add-row" style="margin-top:8px; align-items:center; gap:8px;">
          <input id="cp-show-anchors" type="checkbox" ${state.showAnchors ? "checked" : ""} />
          <label class="small" for="cp-show-anchors">Show Coast Pilot Anchors</label>
        </div>

        <div class="row lm-add-row" style="margin-top:8px; align-items:center; gap:8px;">
          <button id="cp-mode-point" class="btn ${state.interactionMode === "point" ? "primary" : ""}" type="button" title="Point mode" aria-label="Point mode" style="min-width:42px; display:inline-flex; align-items:center; justify-content:center;">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <circle cx="8" cy="8" r="2.5" fill="currentColor"></circle>
              <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"></path>
            </svg>
          </button>
          <button id="cp-mode-route" class="btn ${state.interactionMode === "route" ? "primary" : ""}" type="button" title="Route mode" aria-label="Route mode" style="min-width:42px; display:inline-flex; align-items:center; justify-content:center;">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <circle cx="3" cy="12.5" r="1.6" fill="currentColor"></circle>
              <circle cx="12.7" cy="3.2" r="1.6" fill="currentColor"></circle>
              <path d="M4.7 11.2c1.1-1.3 2.4-2.6 3.7-3.8c1.1-1 2-1.7 3-2.6" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"></path>
            </svg>
          </button>
          <button id="cp-mode-cancel" class="btn ${state.interactionMode === "none" ? "primary" : ""}" type="button" title="Stop drawing" aria-label="Stop drawing" style="min-width:42px;">&times;</button>
          <div class="small">Mode: <strong>${esc(state.interactionMode === "none" ? "idle" : state.interactionMode)}</strong>${state.interactionMode === "route" ? ` &bull; Draft points: ${draftRoutePoints.length}` : ""}</div>
        </div>
        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="cp-anchor-color-mode">Anchor colors</label>
          <select id="cp-anchor-color-mode">
            <option value="feature-class" ${state.anchorColorMode === "feature-class" ? "selected" : ""}>Feature Class</option>
            <option value="single" ${state.anchorColorMode === "single" ? "selected" : ""}>Single Color</option>
          </select>
        </div>
        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <div class="small">Feature Layers (${classRows.length})</div>
          <div style="max-height:200px; overflow:auto; border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px; display:grid; gap:6px;">
            ${classRows.length ? classRows.map((row) => `
              <div style="display:grid; grid-template-columns:auto auto 1fr auto; align-items:center; gap:8px;">
                <input class="cp-feature-toggle" data-feature-class="${esc(row.name)}" type="checkbox" ${state.anchorClassEnabled[row.name] === false ? "" : "checked"} />
                <input class="cp-feature-color" data-feature-class="${esc(row.name)}" type="color" value="${esc(state.anchorClassColor[row.name] || "#94a3b8")}" style="width:26px; height:20px; padding:0; border:none; background:transparent;" />
                <div class="small">${esc(row.name)}</div>
                <div class="small" style="opacity:.75;">${row.count}</div>
              </div>
            `).join("") : `<div class="small" style="opacity:.8;">Enable Show Coast Pilot Anchors to load feature layers.</div>`}
          </div>
        </div>

        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="cp-click-radius">Click search radius (nm)</label>
          <input id="cp-click-radius" type="number" min="1" max="80" step="1" value="${esc(state.clickRadiusNm)}" />
        </div>

        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="cp-route-sample">Route sample spacing (nm)</label>
          <input id="cp-route-sample" type="number" min="1" max="30" step="1" value="${esc(state.routeSampleNm)}" />
        </div>

        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="cp-route-radius">Route excerpt radius (nm)</label>
          <input id="cp-route-radius" type="number" min="1" max="80" step="1" value="${esc(state.routeRadiusNm)}" />
        </div>

        <div class="row lm-add-row" style="margin-top:8px; flex-direction:column; align-items:stretch; gap:4px;">
          <label class="small" for="cp-voice-select">Voice</label>
          <select id="cp-voice-select" ${hasSpeechSupport() ? "" : "disabled"}>
            <option value="">System default</option>
            ${voiceOptions.map((v) => `
              <option value="${esc(v.voiceURI)}" ${String(state.speechVoiceUri) === String(v.voiceURI) ? "selected" : ""}>${esc(`${v.lang || "--"} | ${v.name || "Voice"}`)}</option>
            `).join("")}
          </select>
        </div>

        <div class="row lm-add-row" style="margin-top:8px; gap:6px;">
          <button id="cp-build-index" class="btn" ${loading ? "disabled" : ""}>Build/Refresh Index</button>
          <button id="cp-route-guide" class="btn" ${loading || draftRoutePoints.length < 2 ? "disabled" : ""}>Generate Route Guide</button>
          <button id="cp-clear-route" class="btn">Clear Route</button>
        </div>

        <div class="small" style="margin-top:8px;">${esc(statsText)}</div>
        <div class="small" style="margin-top:4px; min-height:16px;"><strong>Status:</strong> ${esc(status)}</div>
        <div style="margin-top:6px;">
          <div style="height:8px; background:rgba(148,163,184,.25); border-radius:999px; overflow:hidden;">
            <div style="height:100%; width:${progressValue.toFixed(1)}%; background:${isIndexLoading ? "linear-gradient(90deg,#22d3ee,#3b82f6)" : "linear-gradient(90deg,#22c55e,#16a34a)"}; transition:width .2s ease;"></div>
          </div>
          <div class="small" style="margin-top:3px; opacity:.8;">${isIndexLoading ? `Loading Coast Pilot index... ${Math.round(progressValue)}%` : (indexReady ? "Index loaded" : "Index idle")}</div>
        </div>
      </details>

      <details class="card lt-collapsible" open style="margin-top:8px; padding:8px 10px;">
        <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">
          Click Excerpt (${pointGuides.length})
        </summary>
        <div class="small" style="margin-top:6px;">Use point mode, click once to place halo and generate a guide.</div>
        <div style="margin-top:6px; display:grid; gap:6px;">
          ${pointGuides.length ? pointGuides.map((g, gi) => `
            <details class="lt-collapsible" style="border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;" ${gi === 0 ? "open" : ""}>
              <summary class="small" style="cursor:pointer; user-select:none;">
                Point Guide ${pointGuides.length - gi} &bull; ${g.lat.toFixed(5)}, ${g.lon.toFixed(5)} &bull; ${g.radiusNm} nm &bull; ${g.rows.length} anchors
              </summary>
              <div class="row" style="margin-top:6px; gap:6px; align-items:center;">
                <button class="btn cp-speech-control" data-action="toggle" data-kind="point" data-guide-id="${esc(g.id)}" ${g.rows.length ? "" : "disabled"}>
                  ${speechState.activeKind === "point" && String(speechState.activeGuideId) === String(g.id) ? (speechState.isPaused ? "Resume" : "Pause") : "Play"}
                </button>
                <button class="btn cp-speech-control" data-action="prev" data-kind="point" data-guide-id="${esc(g.id)}" ${g.rows.length ? "" : "disabled"}>&lsaquo;</button>
                <button class="btn cp-speech-control" data-action="next" data-kind="point" data-guide-id="${esc(g.id)}" ${g.rows.length ? "" : "disabled"}>&rsaquo;</button>
                <select class="cp-speech-rate" data-kind="point" data-guide-id="${esc(g.id)}" ${g.rows.length ? "" : "disabled"}>
                  ${SPEECH_RATES.map((rate) => `<option value="${rate}" ${Math.abs(getGuideSpeechRate(g) - rate) < 0.001 ? "selected" : ""}>${rate.toFixed(1)}x</option>`).join("")}
                </select>
                <button class="btn cp-export-point" data-guide-id="${esc(g.id)}" ${loading ? "disabled" : ""}>Export PDF</button>
                <div class="small">Anchor ${g.rows.length ? (getGuideSpeechIndex(g) + 1) : 0} / ${g.rows.length}</div>
              </div>
              <div style="margin-top:6px; display:grid; gap:6px;">
                ${g.rows.length ? g.rows.map((r, ri) => `
                  <div class="cp-guide-row" data-anchor-key="${esc(r.key)}" data-active-speech="${speechState.activeKind === "point" && String(speechState.activeGuideId) === String(g.id) && getGuideSpeechIndex(g) === ri ? "1" : "0"}" style="border:1px solid ${speechState.activeKind === "point" && String(speechState.activeGuideId) === String(g.id) && getGuideSpeechIndex(g) === ri ? "rgba(59,130,246,.85)" : "rgba(148,163,184,.15)"}; border-radius:6px; padding:6px; ${speechState.activeKind === "point" && String(speechState.activeGuideId) === String(g.id) && getGuideSpeechIndex(g) === ri ? "box-shadow:0 0 0 1px rgba(59,130,246,.35);" : ""}">
                    <div class="small"><strong>${esc(r.featureName)}</strong> &bull; ${esc(r.featureClass || "Unknown")} &bull; ${r.distanceNm.toFixed(2)} nm</div>
                    <div class="small">${esc(`Book ${r.bookNum} | ${r.chapterTitle} | ${r.paraIndex || "para"}`)}</div>
                    <div class="small" style="margin-top:4px;">${esc(r.excerpt)}</div>
                  </div>
                `).join("") : `<div class="small">No anchors inside this point halo.</div>`}
              </div>
            </details>
          `).join("") : `<div class="small">No point guides yet.</div>`}
        </div>
      </details>

      <details class="card lt-collapsible" open style="margin-top:8px; padding:8px 10px;">
        <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">
          Route Guides (${routeGuides.length})
        </summary>
        <div class="small" style="margin-top:6px;">Use route mode, click waypoints, double-click to finish and generate.</div>
        <div style="margin-top:6px; display:grid; gap:6px;">
          ${routeGuides.length ? routeGuides.map((g, gi) => `
            <details class="lt-collapsible" style="border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;" ${gi === 0 ? "open" : ""}>
              <summary class="small" style="cursor:pointer; user-select:none;">
                Route Guide ${routeGuides.length - gi} &bull; ${g.points.length} waypoints &bull; ${g.radiusNm} nm halo &bull; ${g.rows.length} anchors
              </summary>
              <div class="row" style="margin-top:6px; gap:6px; align-items:center;">
                <button class="btn cp-speech-control" data-action="toggle" data-kind="route" data-guide-id="${esc(g.id)}" ${g.rows.length ? "" : "disabled"}>
                  ${speechState.activeKind === "route" && String(speechState.activeGuideId) === String(g.id) ? (speechState.isPaused ? "Resume" : "Pause") : "Play"}
                </button>
                <button class="btn cp-speech-control" data-action="prev" data-kind="route" data-guide-id="${esc(g.id)}" ${g.rows.length ? "" : "disabled"}>&lsaquo;</button>
                <button class="btn cp-speech-control" data-action="next" data-kind="route" data-guide-id="${esc(g.id)}" ${g.rows.length ? "" : "disabled"}>&rsaquo;</button>
                <select class="cp-speech-rate" data-kind="route" data-guide-id="${esc(g.id)}" ${g.rows.length ? "" : "disabled"}>
                  ${SPEECH_RATES.map((rate) => `<option value="${rate}" ${Math.abs(getGuideSpeechRate(g) - rate) < 0.001 ? "selected" : ""}>${rate.toFixed(1)}x</option>`).join("")}
                </select>
                <button class="btn cp-export-route" data-guide-id="${esc(g.id)}" ${loading ? "disabled" : ""}>Export PDF</button>
                <div class="small">Anchor ${g.rows.length ? (getGuideSpeechIndex(g) + 1) : 0} / ${g.rows.length}</div>
              </div>
              <div class="row" style="margin-top:6px; gap:6px; align-items:center; flex-wrap:wrap;">
                <select class="cp-narrative-language" data-guide-id="${esc(g.id)}" ${g.narrativeLoading ? "disabled" : ""}>
                  ${NARRATIVE_LANG_OPTIONS.map((opt) => `<option value="${esc(opt.value)}" ${String(g.narrativeLanguage || "en") === opt.value ? "selected" : ""}>${esc(opt.label)}</option>`).join("")}
                </select>
                <button class="btn cp-narrative-generate" data-guide-id="${esc(g.id)}" ${g.rows.length && !g.narrativeLoading ? "" : "disabled"}>${g.narrativeLoading ? "Generating..." : "Generate Narrative"}</button>
                <button class="btn cp-narrative-speak" data-guide-id="${esc(g.id)}" ${g.narrativeSummary ? "" : "disabled"}>
                  ${String(narrativeSpeechState.activeGuideId) === String(g.id) ? (narrativeSpeechState.isPaused ? "Resume Narrative" : "Pause Narrative") : "Read Narrative"}
                </button>
              </div>
              ${g.narrativeSummary ? `
                <div style="margin-top:6px; border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:8px; background:rgba(15,23,42,.18);">
                  <div class="small"><strong>${esc(g.narrativeSummary.title || "Narrative Summary")}</strong> <span style="opacity:.8;">(${esc(String(g.narrativeSource || "unknown").toUpperCase())})</span></div>
                  ${g.narrativeWarning ? `<div class="small" style="margin-top:4px; color:#fbbf24;">${esc(g.narrativeWarning)}</div>` : ""}
                  <div class="small" style="margin-top:4px;">${esc(g.narrativeSummary.overview || "")}</div>
                  <div class="small" style="margin-top:6px;">${esc(g.narrativeSummary.narrative || "")}</div>
                </div>
              ` : ""}
              <div style="margin-top:6px; display:grid; gap:6px;">
                ${g.rows.length ? g.rows.map((r, ri) => `
                  <div class="cp-guide-row" data-anchor-key="${esc(r.key)}" data-active-speech="${speechState.activeKind === "route" && String(speechState.activeGuideId) === String(g.id) && getGuideSpeechIndex(g) === ri ? "1" : "0"}" style="border:1px solid ${speechState.activeKind === "route" && String(speechState.activeGuideId) === String(g.id) && getGuideSpeechIndex(g) === ri ? "rgba(59,130,246,.85)" : "rgba(148,163,184,.15)"}; border-radius:6px; padding:6px; ${speechState.activeKind === "route" && String(speechState.activeGuideId) === String(g.id) && getGuideSpeechIndex(g) === ri ? "box-shadow:0 0 0 1px rgba(59,130,246,.35);" : ""}">
                    <div class="small"><strong>Mile ${r.mileNm.toFixed(1)} nm</strong> &bull; ${esc(r.featureName)} &bull; ${esc(r.featureClass || "Unknown")} &bull; ${r.distanceNm.toFixed(2)} nm off-route</div>
                    <div class="small">${esc(`Book ${r.bookNum} | ${r.chapterTitle} | ${r.paraIndex || "para"}`)}</div>
                    <div class="small" style="margin-top:4px;">${esc(r.excerpt)}</div>
                  </div>
                `).join("") : `<div class="small">No anchors inside this route halo corridor.</div>`}
              </div>
            </details>
          `).join("") : `<div class="small">No route guides yet.</div>`}
        </div>
      </details>

      <details class="card lt-collapsible" style="margin-top:8px; padding:8px 10px;">
        <summary class="lm-title lt-section-summary" style="margin:0; cursor:pointer; user-select:none;">
          Diagnostics (${diagLog.length})
        </summary>
        <div class="row" style="margin-top:6px; gap:6px;">
          <button id="cp-log-copy" class="btn" type="button">Copy</button>
          <button id="cp-log-clear" class="btn" type="button">Clear</button>
        </div>
        <textarea id="cp-log-text" readonly style="margin-top:6px; width:100%; height:180px; resize:vertical; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size:11px;">${esc(formatLog())}</textarea>
      </details>
    `;

    const pointModeBtn = containerEl.querySelector("#cp-mode-point");
    const routeModeBtn = containerEl.querySelector("#cp-mode-route");
    const cancelModeBtn = containerEl.querySelector("#cp-mode-cancel");
    const showAnchorsEl = containerEl.querySelector("#cp-show-anchors");
    const anchorColorModeEl = containerEl.querySelector("#cp-anchor-color-mode");
    const clickRadiusEl = containerEl.querySelector("#cp-click-radius");
    const routeSampleEl = containerEl.querySelector("#cp-route-sample");
    const routeRadiusEl = containerEl.querySelector("#cp-route-radius");
    const voiceSelectEl = containerEl.querySelector("#cp-voice-select");
    const featureToggleEls = Array.from(containerEl.querySelectorAll(".cp-feature-toggle"));
    const featureColorEls = Array.from(containerEl.querySelectorAll(".cp-feature-color"));
    const reportRows = Array.from(containerEl.querySelectorAll(".cp-guide-row"));
    const speechControls = Array.from(containerEl.querySelectorAll(".cp-speech-control"));
    const speechRateControls = Array.from(containerEl.querySelectorAll(".cp-speech-rate"));
    const exportPointBtns = Array.from(containerEl.querySelectorAll(".cp-export-point"));
    const exportRouteBtns = Array.from(containerEl.querySelectorAll(".cp-export-route"));
    const narrativeLangEls = Array.from(containerEl.querySelectorAll(".cp-narrative-language"));
    const narrativeGenerateBtns = Array.from(containerEl.querySelectorAll(".cp-narrative-generate"));
    const narrativeSpeakBtns = Array.from(containerEl.querySelectorAll(".cp-narrative-speak"));

    pointModeBtn?.addEventListener("click", () => {
      setInteractionMode(state.interactionMode === "point" ? "none" : "point");
      render();
    });
    routeModeBtn?.addEventListener("click", () => {
      setInteractionMode(state.interactionMode === "route" ? "none" : "route");
      render();
    });
    cancelModeBtn?.addEventListener("click", () => {
      clearDraftInteraction();
      setInteractionMode("none");
      render();
    });
    showAnchorsEl?.addEventListener("change", async () => {
      state.showAnchors = !!showAnchorsEl.checked;
      saveState(state);
      if (state.showAnchors && !indexReady) {
        try {
          loading = true;
          setStatus("Loading Coast Pilot anchor library...");
          render();
          await ensureIndexBuilt();
        } finally {
          loading = false;
        }
      }
      updateAnchorLayer();
      render();
    });
    anchorColorModeEl?.addEventListener("change", () => {
      state.anchorColorMode = String(anchorColorModeEl.value || "feature-class") === "single" ? "single" : "feature-class";
      saveState(state);
      updateAnchorLayer();
      render();
    });
    clickRadiusEl?.addEventListener("change", () => {
      state.clickRadiusNm = clamp(Number(clickRadiusEl.value || state.clickRadiusNm || 20), 1, 80);
      saveState(state);
      render();
    });
    routeSampleEl?.addEventListener("change", () => {
      state.routeSampleNm = clamp(Number(routeSampleEl.value || state.routeSampleNm || 8), 1, 30);
      saveState(state);
      render();
    });
    routeRadiusEl?.addEventListener("change", () => {
      state.routeRadiusNm = clamp(Number(routeRadiusEl.value || state.routeRadiusNm || 16), 1, 80);
      saveState(state);
      render();
    });
    featureToggleEls.forEach((el) => {
      el.addEventListener("change", () => {
        const cls = String(el.getAttribute("data-feature-class") || "");
        if (!cls) return;
        state.anchorClassEnabled[cls] = !!el.checked;
        saveState(state);
        updateAnchorLayer();
        render();
      });
    });
    featureColorEls.forEach((el) => {
      el.addEventListener("change", () => {
        const cls = String(el.getAttribute("data-feature-class") || "");
        if (!cls) return;
        state.anchorClassColor[cls] = String(el.value || "#94a3b8");
        saveState(state);
        updateAnchorLayer();
        render();
      });
    });
    voiceSelectEl?.addEventListener("change", () => {
      state.speechVoiceUri = String(voiceSelectEl.value || "");
      saveState(state);
      const kind = speechState.activeKind;
      const guideId = speechState.activeGuideId;
      if (kind && guideId && !speechState.isPaused) {
        speakGuideRow(kind, guideId, speechState.activeIndex);
      } else if (narrativeSpeechState.activeGuideId && !narrativeSpeechState.isPaused) {
        toggleNarrativeSpeech(String(narrativeSpeechState.activeGuideId));
      } else {
        render();
      }
    });

    containerEl.querySelector("#cp-build-index")?.addEventListener("click", async () => {
      try {
        loading = true;
        render();
        await ensureIndexBuilt();
        updateAnchorLayer();
      } finally {
        loading = false;
        render();
      }
    });
    containerEl.querySelector("#cp-route-guide")?.addEventListener("click", () => {
      generateRouteGuide();
    });
    containerEl.querySelector("#cp-clear-route")?.addEventListener("click", () => {
      clearRoute();
    });
    containerEl.querySelector("#cp-log-copy")?.addEventListener("click", async () => {
      const ok = await copyLogText(formatLog());
      setStatus(ok ? "Diagnostics copied to clipboard." : "Copy failed.", { level: ok ? "info" : "warn", log: true });
      render();
    });
    containerEl.querySelector("#cp-log-clear")?.addEventListener("click", () => {
      diagLog = [];
      setStatus("Diagnostics log cleared.", { level: "info", log: true });
      render();
    });
    reportRows.forEach((row) => {
      const key = row.getAttribute("data-anchor-key");
      row.addEventListener("mouseenter", () => {
        hoveredAnchorKey = String(key || "");
        row.style.borderColor = "rgba(59,130,246,.85)";
        row.style.boxShadow = "0 0 0 1px rgba(59,130,246,.35)";
        syncMapHighlight();
      });
      row.addEventListener("mouseleave", () => {
        hoveredAnchorKey = "";
        if (row.getAttribute("data-active-speech") === "1") {
          row.style.borderColor = "rgba(59,130,246,.85)";
          row.style.boxShadow = "0 0 0 1px rgba(59,130,246,.35)";
        } else {
          row.style.borderColor = "rgba(148,163,184,.15)";
          row.style.boxShadow = "none";
        }
        syncMapHighlight();
      });
    });
    speechControls.forEach((btn) => {
      btn.addEventListener("click", () => {
        const action = String(btn.getAttribute("data-action") || "");
        const kind = String(btn.getAttribute("data-kind") || "point");
        const guideId = String(btn.getAttribute("data-guide-id") || "");
        if (!guideId) return;
        if (action === "toggle") {
          toggleGuideSpeech(kind, guideId);
          return;
        }
        if (action === "prev") {
          jumpGuideSpeech(kind, guideId, -1);
          return;
        }
        if (action === "next") {
          jumpGuideSpeech(kind, guideId, 1);
        }
      });
    });
    speechRateControls.forEach((el) => {
      el.addEventListener("change", () => {
        const kind = String(el.getAttribute("data-kind") || "point");
        const guideId = String(el.getAttribute("data-guide-id") || "");
        const guide = getGuide(kind, guideId);
        if (!guide) return;
        guide.speechRate = clamp(Number(el.value || 1), 0.5, 2.0);
        const sameGuide = speechState.activeKind === kind && String(speechState.activeGuideId) === String(guide.id);
        if (sameGuide && speechState.utterance && !speechState.isPaused) {
          speakGuideRow(kind, guide.id, getGuideSpeechIndex(guide));
        } else {
          render();
        }
      });
    });
    exportRouteBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const guideId = String(btn.getAttribute("data-guide-id") || "");
        if (!guideId) return;
        exportRouteGuidePdf(guideId);
      });
    });
    exportPointBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const guideId = String(btn.getAttribute("data-guide-id") || "");
        if (!guideId) return;
        exportPointGuidePdf(guideId);
      });
    });
    narrativeLangEls.forEach((el) => {
      el.addEventListener("change", () => {
        const guideId = String(el.getAttribute("data-guide-id") || "");
        const g = getGuide("route", guideId);
        if (!g) return;
        g.narrativeLanguage = String(el.value || "en");
        render();
      });
    });
    narrativeGenerateBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const guideId = String(btn.getAttribute("data-guide-id") || "");
        if (!guideId) return;
        generateRouteNarrative(guideId);
      });
    });
    narrativeSpeakBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const guideId = String(btn.getAttribute("data-guide-id") || "");
        if (!guideId) return;
        toggleNarrativeSpeech(guideId);
      });
    });
  }

  function mount(nextContainer) {
    containerEl = nextContainer;
    const map = app.map.instance;
    state.showAnchors = false;
    saveState(state);
    map.addLayer(interactionLayer);
    map.addLayer(guidesLayer);
    map.addLayer(anchorLayer);
    map.addLayer(hoverLayer);
    redrawInteraction();
    redrawGuides();
    mapClickHandler = onMapClick;
    mapDblClickHandler = onMapDoubleClick;
    mapMouseMoveHandler = onMapMouseMove;
    map.on("click", mapClickHandler);
    map.on("dblclick", mapDblClickHandler);
    map.on("mousemove", mapMouseMoveHandler);
    mapWheelHandler = (ev) => {
      if (!ev?.ctrlKey) return;
      if (state.interactionMode !== "point") return;
      const dir = Number(ev.deltaY) > 0 ? -1 : 1;
      const next = clamp(Number(state.clickRadiusNm || 20) + dir, 1, 80);
      if (next === state.clickRadiusNm) return;
      ev.preventDefault();
      ev.stopPropagation?.();
      ev.stopImmediatePropagation?.();
      state.clickRadiusNm = next;
      saveState(state);
      redrawInteraction();
      setStatus(`Point radius: ${state.clickRadiusNm.toFixed(0)} nm`);
      if (containerEl) {
        const input = containerEl.querySelector("#cp-click-radius");
        if (input) input.value = String(state.clickRadiusNm);
      }
    };
    map.getContainer().addEventListener("wheel", mapWheelHandler, { passive: false, capture: true });
    mapMoveHandler = () => {
      if (indexReady && state.showAnchors) updateAnchorLayer();
    };
    map.on("moveend", mapMoveHandler);
    applyMapCursor();
    refreshVoices({ rerender: false });
    if (hasSpeechSupport()) {
      voicesChangedHandler = () => refreshVoices({ rerender: true });
      window.speechSynthesis.addEventListener("voiceschanged", voicesChangedHandler);
    }
    if (state.interactionMode === "route") map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
    if (state.interactionMode === "point") map.scrollWheelZoom.disable();
    else map.scrollWheelZoom.enable();
    pushLog("info", "Coast Pilot panel mounted.");
    setStatus("Coast Pilot Explorer ready. Choose point or route mode to draw.");
    render();
  }

  function destroy() {
    stopSpeech({ cancel: true, rerender: false });
    stopNarrativeSpeech({ cancel: false, rerender: false });
    const map = app.map.instance;
    if (mapClickHandler) map.off("click", mapClickHandler);
    if (mapDblClickHandler) map.off("dblclick", mapDblClickHandler);
    if (mapMouseMoveHandler) map.off("mousemove", mapMouseMoveHandler);
    if (mapMoveHandler) map.off("moveend", mapMoveHandler);
    if (mapWheelHandler) map.getContainer().removeEventListener("wheel", mapWheelHandler, { capture: true });
    mapClickHandler = null;
    mapDblClickHandler = null;
    mapMouseMoveHandler = null;
    mapMoveHandler = null;
    mapWheelHandler = null;
    map.removeLayer(interactionLayer);
    map.removeLayer(guidesLayer);
    map.removeLayer(anchorLayer);
    map.removeLayer(hoverLayer);
    if (hasSpeechSupport() && voicesChangedHandler) {
      try {
        window.speechSynthesis.removeEventListener("voiceschanged", voicesChangedHandler);
      } catch {}
    }
    voicesChangedHandler = null;
    map.doubleClickZoom.enable();
    map.scrollWheelZoom.enable();
    map.getContainer().style.cursor = "";
  }

  return {
    mount,
    destroy,
  };
}


